import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { psrReportAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const MONTHS_FULL  = ['January','February','March','April','May','June',
                       'July','August','September','October','November','December'];

// ── Status config ─────────────────────────────────────────────
const STATUS = {
  approved:  { dot: '#22c55e', label: 'Approved',    badge: { bg: '#dcfce7', color: '#15803d' } },
  submitted: { dot: '#f59e0b', label: 'In Review',   badge: { bg: '#fef9c3', color: '#92400e' } },
  draft:     { dot: '#94a3b8', label: 'Draft',        badge: { bg: '#f1f5f9', color: '#475569' } },
  rejected:  { dot: '#ef4444', label: 'Rejected',    badge: { bg: '#fee2e2', color: '#b91c1c' } },
  empty:     { dot: '#e2e8f0', label: '',             badge: null },
};

function getStatus(report) {
  if (!report) return 'empty';
  return report.status || 'draft';
}

export default function PSRReport() {
  const { user }       = useAuth();
  const [reports, setReports]       = useState({});   // key: "YYYY_M" → report
  const [loading, setLoading]       = useState(true);
  const [error,   setError]         = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const now          = new Date();
  const currentYear  = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Derive joining date from user account creation
  const joinDate   = user?.created_at ? new Date(user.created_at) : new Date(currentYear - 2, 0, 1);
  const joinYear   = joinDate.getFullYear();
  const joinMonth  = joinDate.getMonth() + 1;

  const yearRange  = Array.from({ length: currentYear - joinYear + 1 }, (_, i) => joinYear + i).reverse();

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await psrReportAPI.getMyReports();
      const rows = res.data?.data?.reports || [];
      const map  = {};
      rows.forEach(r => { map[`${r.report_year}_${r.report_month}`] = r; });
      setReports(map);
    } catch {
      setError('Failed to load your reports.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  if (loading) return (
    <div className="text-center py-5">
      <div className="spinner-border text-primary" />
    </div>
  );

  if (error) return (
    <div className="alert alert-danger">{error}</div>
  );

  const totalMonths   = Object.keys(reports).length;
  const approvedCount = Object.values(reports).filter(r => r.status === 'approved').length;
  const pendingCount  = Object.values(reports).filter(r => r.status === 'submitted').length;

  // Find the most recent submitted/approved report for auto-populate
  const lastReport = useMemo(() => {
    return Object.values(reports)
      .filter(r => r.status === 'approved' || r.status === 'submitted')
      .sort((a, b) => {
        if (Number(a.report_year) !== Number(b.report_year)) return Number(b.report_year) - Number(a.report_year);
        return Number(b.report_month) - Number(a.report_month);
      })[0] ?? null;
  }, [reports]);

  return (
    <div>
      {/* Page header */}
      <div className="page-header">
        <h2>Monthly Reports</h2>
        <div className="sub">Your status reports — one per month from your joining date</div>
      </div>

      {/* Summary pills */}
      <div className="d-flex gap-2 flex-wrap mb-3">
        <span style={{ ...pill, background: '#f0fdf4', color: '#15803d', border: '1px solid #bbf7d0' }}>
          <span style={{ ...dot, background: '#22c55e' }} />
          {approvedCount} Approved
        </span>
        <span style={{ ...pill, background: '#fef9c3', color: '#92400e', border: '1px solid #fde68a' }}>
          <span style={{ ...dot, background: '#f59e0b' }} />
          {pendingCount} In Review
        </span>
        <span style={{ ...pill, background: '#f8fafc', color: '#475569', border: '1px solid #e2e8f0' }}>
          <span style={{ ...dot, background: '#94a3b8' }} />
          {totalMonths - approvedCount - pendingCount} Not Submitted
        </span>
      </div>

      {/* Card */}
      <div style={cardStyle}>

        {/* Year tabs */}
        <div style={{ padding: '12px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', gap: 8, flexWrap: 'wrap', background: '#fafafa' }}>
          {yearRange.map(y => (
            <button
              key={y}
              onClick={() => setSelectedYear(y)}
              style={{
                padding: '4px 14px', borderRadius: 999, fontSize: '.75rem', fontWeight: 600,
                border: 'none', cursor: 'pointer', transition: 'all .15s',
                background:  selectedYear === y ? '#2563eb' : '#f1f5f9',
                color:       selectedYear === y ? '#fff'    : '#64748b',
              }}
            >
              {y}
            </button>
          ))}
        </div>

        {/* Monthly rows */}
        <div>
          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
            const isFuture = selectedYear > currentYear ||
              (selectedYear === currentYear && month > currentMonth);
            const isBeforeJoining = selectedYear < joinYear ||
              (selectedYear === joinYear && month < joinMonth);
            const key    = `${selectedYear}_${month}`;
            const report = reports[key] || null;
            return (
              <MonthRow
                key={key}
                year={selectedYear}
                month={month}
                report={report}
                lastReport={lastReport}
                isFuture={isFuture}
                isBeforeJoining={isBeforeJoining}
                onSaved={fetchReports}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Month Row ─────────────────────────────────────────────────
function MonthRow({ year, month, report, lastReport, isFuture, isBeforeJoining, onSaved }) {
  const [expanded, setExpanded]   = useState(false);
  const [form,     setForm]       = useState({});
  const [saving,   setSaving]     = useState(false);
  const [saveErr,  setSaveErr]    = useState('');
  const [saveOk,   setSaveOk]     = useState('');

  const status      = getStatus(report);
  const statusCfg   = STATUS[status];
  const isDisabled  = isFuture || isBeforeJoining;
  const isEditable  = !isDisabled && (status === 'empty' || status === 'draft' || status === 'rejected');
  const isReadOnly  = !isDisabled && (status === 'submitted' || status === 'approved');

  function openForm() {
    if (isDisabled) return;
    // For new reports, auto-populate work details from the most recent submitted/approved report
    const seed = report ?? null;
    const auto = !seed ? lastReport : null;
    setForm({
      id:                  seed?.id                  ?? null,
      role_title:          seed?.role_title          ?? auto?.role_title          ?? '',
      client:              seed?.client              ?? auto?.client              ?? '',
      vendor:              seed?.vendor              ?? auto?.vendor              ?? '',
      technology:          seed?.technology          ?? auto?.technology          ?? '',
      project_description: seed?.project_description ?? '',
      tools_used:          seed?.tools_used          ?? auto?.tools_used          ?? '',
      i9_changes_required: seed?.i9_changes_required === 1 || seed?.i9_changes_required === '1',
      issues:              seed?.issues              ?? '',
      tasks_this_month:    seed?.tasks_this_month    ?? '',
      tasks_next_month:    seed?.tasks_next_month    ?? '',
      i9_change_reason:    seed?.i9_change_reason    ?? '',
      github_link:         seed?.github_link         ?? '',
    });
    setSaveErr('');
    setSaveOk('');
    setExpanded(true);
  }

  function closeForm() { setExpanded(false); setSaveErr(''); setSaveOk(''); }

  async function handleSave(action) {
    if (action === 'submit' && !form.github_link?.trim()) {
      setSaveErr('GitHub link is required to submit.');
      return;
    }
    setSaving(true); setSaveErr(''); setSaveOk('');
    try {
      const fd = new FormData();
      fd.append('action',              action);
      fd.append('report_month',        String(month));
      fd.append('report_year',         String(year));
      if (form.id) fd.append('id',     String(form.id));
      fd.append('role_title',          form.role_title          || '');
      fd.append('client',              form.client              || '');
      fd.append('vendor',              form.vendor              || '');
      fd.append('technology',          form.technology          || '');
      fd.append('project_description', form.project_description || '');
      fd.append('tools_used',          form.tools_used          || '');
      fd.append('i9_changes_required', form.i9_changes_required ? '1' : '0');
      fd.append('issues',              form.issues              || '');
      fd.append('tasks_this_month',    form.tasks_this_month    || '');
      fd.append('tasks_next_month',    form.tasks_next_month    || '');
      fd.append('i9_change_reason',    form.i9_change_reason    || '');
      fd.append('github_link',         form.github_link         || '');

      await psrReportAPI.saveReport(fd);
      setSaveOk(action === 'submit' ? 'Submitted for admin review.' : 'Draft saved.');
      await onSaved();
      setTimeout(() => { setSaveOk(''); closeForm(); }, 1200);
    } catch (err) {
      setSaveErr(err.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  }

  function sf(k, v) { setForm(p => ({ ...p, [k]: v })); }

  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', opacity: isDisabled ? .4 : 1 }}>

      {/* ── Collapsed row ─── */}
      <div
        onClick={() => !expanded && !isDisabled && openForm()}
        style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 20px',
          cursor: isDisabled ? 'default' : expanded ? 'default' : 'pointer',
          background: expanded ? '#eff6ff' : isBeforeJoining ? '#fafafa' : 'transparent',
          transition: 'background .15s',
        }}
        onMouseEnter={e => { if (!isDisabled && !expanded) e.currentTarget.style.background = '#f8fafc'; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = isBeforeJoining ? '#fafafa' : 'transparent'; }}
      >
        {/* Status dot */}
        <div style={{ width: 10, height: 10, borderRadius: '50%', flexShrink: 0, background: statusCfg.dot }} />

        {/* Month name */}
        <div style={{ width: 110, flexShrink: 0, fontWeight: 600, fontSize: '.875rem',
          color: isDisabled ? '#94a3b8' : '#1e293b' }}>
          {MONTHS_FULL[month - 1]}
        </div>

        {/* Summary */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', minWidth: 0 }}>
          {isBeforeJoining ? (
            <span style={{ fontSize: '.75rem', color: '#94a3b8', fontStyle: 'italic' }}>Before joining</span>
          ) : isFuture ? null : status === 'empty' ? (
            <span style={{ fontSize: '.8125rem', color: '#f59e0b', fontStyle: 'italic' }}>Not submitted yet</span>
          ) : (
            <>
              {report?.role_title && (
                <span style={{ fontSize: '.8125rem', color: '#334155', fontWeight: 500 }}>{report.role_title}</span>
              )}
              {report?.client && (
                <span style={{ fontSize: '.8125rem', color: '#64748b' }}>{report.client}</span>
              )}
              {report?.technology && (
                <span style={{ fontSize: '.75rem', color: '#94a3b8' }}>{report.technology}</span>
              )}
              {statusCfg.badge && (
                <span style={{
                  fontSize: '.6875rem', fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                  background: statusCfg.badge.bg, color: statusCfg.badge.color,
                }}>
                  {statusCfg.label}
                </span>
              )}
              {(report?.i9_changes_required === 1 || report?.i9_changes_required === '1') && (
                <span style={miniTag('#ede9fe', '#6d28d9')}>I-9</span>
              )}
              {report?.issues?.trim() && (
                <span style={miniTag('#fff7ed', '#c2410c')}>Issues</span>
              )}
              {report?.github_link?.trim() && (
                <span style={miniTag('#f0fdf4', '#15803d')}>GitHub ✓</span>
              )}
              {report?.status === 'rejected' && report?.rejection_reason && (
                <span style={{ fontSize: '.75rem', color: '#b91c1c', fontStyle: 'italic' }}>
                  Rejected: {report.rejection_reason}
                </span>
              )}
            </>
          )}
        </div>

        {/* Right action */}
        {!isDisabled && (
          <div style={{ flexShrink: 0, marginLeft: 'auto' }}>
            {expanded ? (
              <span style={{ fontSize: '.75rem', color: '#94a3b8' }}>▲</span>
            ) : isReadOnly ? (
              <span style={actionChip('#f1f5f9', '#64748b')}>View</span>
            ) : status !== 'empty' ? (
              <span style={actionChip('#eff6ff', '#2563eb')}>Edit</span>
            ) : (
              <span style={actionChip('#eff6ff', '#2563eb', '#bfdbfe')}>+ Add</span>
            )}
          </div>
        )}
      </div>

      {/* ── Expanded form / view ─── */}
      {expanded && (
        <div style={{ padding: '16px 20px 20px', background: '#fafcff', borderTop: '1px solid #e0eeff' }}>
          <div style={{ fontSize: '.6875rem', fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: '.08em', color: '#64748b', marginBottom: 14 }}>
            {MONTHS_FULL[month - 1]} {year}
            {isReadOnly && (
              <span style={{ ...miniTag('#fef9c3', '#92400e'), marginLeft: 8, textTransform: 'none', letterSpacing: 0 }}>
                {STATUS[status].label} — read only
              </span>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
            <Field label="Role / Title">
              <input className="form-control" value={form.role_title || ''} disabled={isReadOnly}
                onChange={e => sf('role_title', e.target.value)} placeholder="Software Engineer" />
            </Field>
            <Field label="Client">
              <input className="form-control" value={form.client || ''} disabled={isReadOnly}
                onChange={e => sf('client', e.target.value)} placeholder="Client name" />
            </Field>
            <Field label="Vendor">
              <input className="form-control" value={form.vendor || ''} disabled={isReadOnly}
                onChange={e => sf('vendor', e.target.value)} placeholder="Staffing vendor" />
            </Field>
            <Field label="Technology">
              <input className="form-control" value={form.technology || ''} disabled={isReadOnly}
                onChange={e => sf('technology', e.target.value)} placeholder="React, Java…" />
            </Field>
            <Field label="Tools Used">
              <input className="form-control" value={form.tools_used || ''} disabled={isReadOnly}
                onChange={e => sf('tools_used', e.target.value)} placeholder="Jira, Git…" />
            </Field>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
            <Field label="Project Description">
              <textarea className="form-control" rows={3} value={form.project_description || ''} disabled={isReadOnly}
                onChange={e => sf('project_description', e.target.value)} placeholder="Describe your work this month"
                style={{ resize: 'none' }} />
            </Field>
            <Field label="Issues / Concerns">
              <textarea className="form-control" rows={3} value={form.issues || ''} disabled={isReadOnly}
                onChange={e => sf('issues', e.target.value)} placeholder="Any issues this month (leave blank if none)"
                style={{ resize: 'none' }} />
            </Field>
            <Field label="Tasks Completed This Month">
              <textarea className="form-control" rows={3} value={form.tasks_this_month || ''} disabled={isReadOnly}
                onChange={e => sf('tasks_this_month', e.target.value)} placeholder="List tasks you completed this month"
                style={{ resize: 'none' }} />
            </Field>
            <Field label="Tasks for Coming Month">
              <textarea className="form-control" rows={3} value={form.tasks_next_month || ''} disabled={isReadOnly}
                onChange={e => sf('tasks_next_month', e.target.value)} placeholder="List planned tasks for next month"
                style={{ resize: 'none' }} />
            </Field>
          </div>

          <div style={{ display: 'flex', gap: 20, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <CheckField label="I-9 Changes Required" disabled={isReadOnly}
              checked={!!form.i9_changes_required}
              onChange={v => sf('i9_changes_required', v)} />
          </div>

          {form.i9_changes_required && (
            <Field label="I-9 Change Reason" style={{ marginBottom: 12 }}>
              <textarea className="form-control" rows={2} value={form.i9_change_reason || ''} disabled={isReadOnly}
                onChange={e => sf('i9_change_reason', e.target.value)} placeholder="Describe the I-9 change needed"
                style={{ resize: 'none' }} />
            </Field>
          )}

          <Field label={<>GitHub Link {!isReadOnly && <span style={{ color: '#ef4444' }}>*</span>}</>} style={{ marginBottom: 12 }}>
            <input className="form-control" value={form.github_link || ''} disabled={isReadOnly}
              onChange={e => sf('github_link', e.target.value)} placeholder="https://github.com/..." />
          </Field>

          {saveErr && <div className="alert alert-danger py-2 mb-3">{saveErr}</div>}
          {saveOk  && <div className="alert alert-success py-2 mb-3">{saveOk}</div>}

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={closeForm} disabled={saving}
              style={btnStyle('#fff', '#475569', '#e2e8f0')}>
              {isReadOnly ? 'Close' : 'Cancel'}
            </button>
            {!isReadOnly && (
              <>
                <button onClick={() => handleSave('draft')} disabled={saving}
                  style={btnStyle('#f8fafc', '#475569', '#e2e8f0')}>
                  {saving ? 'Saving…' : 'Save Draft'}
                </button>
                <button onClick={() => handleSave('submit')} disabled={saving}
                  style={{ ...btnStyle('#2563eb', '#fff', '#2563eb'), fontWeight: 600 }}>
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Submitting…</>
                    : status === 'rejected' ? 'Resubmit for Review' : 'Submit for Review'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────
function Field({ label, children, style }) {
  return (
    <div style={style}>
      <label style={{ display: 'block', fontSize: '.75rem', fontWeight: 500, color: '#64748b', marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function CheckField({ label, checked, onChange, disabled }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: disabled ? 'default' : 'pointer', userSelect: 'none' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled}
        style={{ width: 15, height: 15, accentColor: '#2563eb', cursor: disabled ? 'default' : 'pointer' }} />
      <span style={{ fontSize: '.875rem', color: '#334155' }}>{label}</span>
    </label>
  );
}

// ── Style helpers ─────────────────────────────────────────────
const cardStyle = {
  background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
  boxShadow: '0 1px 3px rgba(0,0,0,.06)', overflow: 'hidden',
};

const pill = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '4px 12px', borderRadius: 999,
  fontSize: '.75rem', fontWeight: 600,
};

const dot = {
  display: 'inline-block', width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
};

function miniTag(bg, color) {
  return {
    display: 'inline-block', fontSize: '.6875rem', fontWeight: 600,
    padding: '2px 7px', borderRadius: 999, background: bg, color,
  };
}

function actionChip(bg, color, borderColor) {
  return {
    display: 'inline-block', fontSize: '.75rem', fontWeight: 500,
    padding: '3px 10px', borderRadius: 6,
    background: bg, color,
    border: `1px solid ${borderColor || bg}`,
    cursor: 'pointer',
  };
}

function btnStyle(bg, color, border) {
  return {
    padding: '8px 18px', borderRadius: 8, border: `1px solid ${border}`,
    background: bg, color, fontSize: '.8125rem', fontWeight: 500,
    cursor: 'pointer', transition: 'all .15s', display: 'inline-flex', alignItems: 'center',
  };
}
